import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, CircularProgress, Alert, Switch, TextField, Button, FormControl, InputLabel, MenuItem, Select, Stack
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridFilterModel, getGridStringOperators } from '@mui/x-data-grid';
import { videoAdApi } from '../services/api';
import { VideoAdDManager } from '../types';

const defaultPageSize = 25;

const VideoAdManager: React.FC = () => {
  const [rows, setRows] = useState<VideoAdDManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [massUpdating, setMassUpdating] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await videoAdApi.getAll({});
      if (!Array.isArray(res)) throw new Error('Некорректный формат данных');
      setRows(res);
    } catch (e) {
      setError('Ошибка загрузки данных: ' + (e instanceof Error ? e.message : ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleIsActiveChange = async (id: number, isActive: boolean) => {
    try {
      await videoAdApi.updateIsActive(id, isActive);
      fetchData();
    } catch {
      setError('Ошибка обновления статуса активности');
    }
  };

  const handleMassUpdateIsActive = async (newIsActive: boolean) => {
    setMassUpdating(true);
    try {
      await Promise.all(filteredRows.map(row => videoAdApi.updateIsActive(row.videoAdDId, newIsActive)));
      await fetchData();
    } catch {
      setError('Ошибка массового обновления статуса активности');
    } finally {
      setMassUpdating(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'videoAdDId', headerName: 'ID', width: 80 },
    { field: 'adErid', headerName: 'ERID', width: 120 },
    { field: 'advertiserName', headerName: 'Рекламодатель', width: 160 },
    { field: 'adTitle', headerName: 'Реклама', width: 200, flex: 1 },
    { field: 'platformTitle', headerName: 'Платформа', width: 150 },
    { field: 'videoTitle', headerName: 'Видео', width: 200, flex: 1 },
    { field: 'videoExternalId', headerName: 'Video ID', width: 120 },
    { field: 'videoHostingCd', headerName: 'Хостинг', width: 120 },
    {
      field: 'isActive',
      headerName: 'Активен',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.value}
          onChange={(_, checked) => handleIsActiveChange(params.row.videoAdDId, checked)}
          color="primary"
        />
      ),
      sortable: false,
      filterable: false,
    },
    { field: 'createdTs', headerName: 'Создан', width: 150 },
  ];

  // Функция для применения фильтрации DataGrid
  function applyGridFilters(rows: VideoAdDManager[], filterModel: GridFilterModel): VideoAdDManager[] {
    if (!filterModel || !filterModel.items.length) return rows;
    return rows.filter(row => {
      return filterModel.items.every(filter => {
        const column = (filter as any).field;
        if (!filter.value || !column) return true;
        const value = String(row[column as keyof VideoAdDManager] ?? '').toLowerCase();
        const filterValue = String(filter.value).toLowerCase();
        // Только string-операторы (содержит, равно)
        if (filter.operator === 'contains') return value.includes(filterValue);
        if (filter.operator === 'equals') return value === filterValue;
        if (filter.operator === 'startsWith') return value.startsWith(filterValue);
        if (filter.operator === 'endsWith') return value.endsWith(filterValue);
        return true;
      });
    });
  }

  const filteredRows = applyGridFilters(
    rows.filter(row => {
      if (activeFilter === 'all') return true;
      if (activeFilter === 'active') return row.isActive;
      if (activeFilter === 'inactive') return !row.isActive;
      return true;
    }),
    filterModel
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>Менеджер рекламы</Typography>
      <Card>
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box mb={2} display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="active-filter-label">Фильтр по активности</InputLabel>
              <Select
                labelId="active-filter-label"
                value={activeFilter}
                label="Фильтр по активности"
                onChange={e => setActiveFilter(e.target.value as any)}
              >
                <MenuItem value="all">Все</MenuItem>
                <MenuItem value="active">Только активные</MenuItem>
                <MenuItem value="inactive">Только неактивные</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={filteredRows.length === 0 || massUpdating}
                onClick={() => handleMassUpdateIsActive(true)}
              >
                Включить все
              </Button>
              <Button
                variant="contained"
                color="warning"
                size="small"
                disabled={filteredRows.length === 0 || massUpdating}
                onClick={() => handleMassUpdateIsActive(false)}
              >
                Выключить все
              </Button>
            </Stack>
          </Box>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={row => row.videoAdDId}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            initialState={{
              sorting: { sortModel: [{ field: 'createdTs', sort: 'desc' }] },
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default VideoAdManager; 